import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CycleScheme, InfoOutline } from '@yme/assets/icons';
import { santander } from '@yme/assets/icons/payment-options';
import { Alert, AlertType } from '@yme/atoms/alert';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { ProductType } from '@yme/common/constants';
import { FINANCING_ENABLED, LEASING_ENABLED } from '@yme/common/redux/cart/constants';
import { getAttribute, getRTL } from '@yme/common/selectors';
import { getAuthorizedStatus } from '@yme/components/Header/ProfileMenu/redux/selectors';
import { DownloadSectionComponent } from '@yme/molecules/download-section';
import { ProductBanner } from '@yme/molecules/product-banner';
import { NotifyMe } from '@yme/pdp-components/NotifyMe';
import { getProductCardLabels, getProductFilterOptions, getProductQuantity, getSelectedCompatibilityProduct, } from '@yme/pdp-components/ProductCard';
import { COOKIE_HYPERDRIVE_SELECTED_UNIT, INCOMPATIBLE, NEED_CHECK, NEED_REQUIRED_PARTS, } from '@yme/pdp-components/ProductCard/CardAccessories/constants';
import { CycleSchemeBanner } from '@yme/pdp-components/ProductCard/components/Cyclescheme';
import { LeasingPayments } from '@yme/pdp-components/ProductCard/components/LeasingPayments';
import { PayeverCalculator } from '@yme/pdp-components/ProductCard/components/PayeverCalculator';
import { WarrantyInformation } from '@yme/pdp-components/ProductCard/components/WarrantyInformation';
import { TestRideMessageForVariations } from '@yme/pdp-components/TestRideMessageForVariations';
import { getCommonLabels, getEcommerceEnabled, getIsCycleSchemeEnabled, getIsGetCurrentCountryRootGB, getIsInstallmentEnabled, getIsKioskVersion, getIsOmnibusRegulationEnabled, getRootLang, } from '@yme/selectors/common';
import { getMotorcycleVariationsStatus } from '@yme/selectors/products';
import { useTranslation } from '@yme/services/i18n';
import { colorCoreBlack300, colorCoreBlack900Base } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularTextLevel3 } from '@yme/tokens/typography';
import { formatPrice, getFormattedPrice } from '@yme/utils/formatPrice';
import { getFilterOptionByProductType, getLabels } from '../../helpers';
import { AllProductUnitPrices } from '../AllProductUnitPrices';
import { ButtonsPanel } from '../ButtonsPanel';
import { ProductFilter } from '../ProductFilter';
import { ProductYouFinanceInfo } from '../ProductYouFinanceInfo';
import { UnitPrice } from '../UnitPrice';
import { PriceDisclaimerText } from '../UnitPrice/PriceDisclaimerText';
import { UnitPriceWrapperStyled } from '../UnitPrice/styled';
import { COUNT_PLACEHOLDER, INITIAL_CURRENT_PRODUCT_STATE, LOW_STOCK_THRESHOLD, SANTANDER, } from './constants';
import { getLastMonthDiscountObject, getMasterProduct, prepareProductsUrls } from './helpers';
import { CardPanelWrapperStyled, PaymentOptionsStyled } from './styled';
export function CardPanel({ priceExtraInfo, priceExtraInfoTooltip, labels, language, isMaxStockValue, hyperdriveEndpoints, productVariantsByColor, paymentOptions, plpUrl = '', smartfitKey, productType, productNotifyMeDisclaimer, openSelectModelModal, addRequiredProduct, removeRequiredProduct, compatibilityAlert, sizeGuideAssetPath, isAccessoryHideProductCompatibility, warrantyInfo, privacyConsentsModel, productName, isRequiredProductExist, requiredProducts = [], addedRequiredProducts = [], downloadSectionItems = [], payeverCalculatorModel, fasletModel, campaignDisclaimer, motorcyclesInfoLink, scooterPackagesInfoLink, }) {
    const [currentProduct, setCurrentProduct] = useState(INITIAL_CURRENT_PRODUCT_STATE);
    const [accessoriesCartState, setAccessoriesCartState] = useState('');
    const { t } = useTranslation();
    const isRightToLeft = useSelector(getRTL);
    const commonLabels = useSelector(getCommonLabels);
    const rootLanguage = useSelector(getRootLang);
    const isOmnibusRegulationEnabled = useSelector(getIsOmnibusRegulationEnabled);
    const checkCompatibilityProduct = useSelector(getSelectedCompatibilityProduct);
    const productQuantity = useSelector(getProductQuantity);
    const isAuthorized = useSelector(getAuthorizedStatus);
    const ecommerceEnabled = useSelector(getEcommerceEnabled);
    const isFinancingEnabled = useSelector(getAttribute(FINANCING_ENABLED));
    const isLeasingEnabled = useSelector(getAttribute(LEASING_ENABLED));
    const isInstallmentEnabled = useSelector(getIsInstallmentEnabled);
    const isIsoCountryCodeGB = useSelector(getIsGetCurrentCountryRootGB);
    const isCycleSchemeEnabled = useSelector(getIsCycleSchemeEnabled);
    const isKioskVersion = useSelector(getIsKioskVersion);
    const isGroupingProducts = useSelector(getMotorcycleVariationsStatus);
    const { pathname = '', hash = '' } = useLocation();
    const isUnit = productType === ProductType.UNIT;
    const isArchiveProduct = currentProduct?.archiveProduct && !isGroupingProducts;
    const isShownCycleScheme = isUnit && isIsoCountryCodeGB && isCycleSchemeEnabled && isLeasingEnabled;
    const isShownLeasingPayments = !isKioskVersion && isUnit && !isIsoCountryCodeGB;
    const { payeverCalculatorConfig, isCalculatorEnabled, labels: payeverLabels = {}, isShowStub, } = payeverCalculatorModel || {};
    const { payeverCalculator_stubDescription_label, payeverCalculator_stubHeader_label } = payeverLabels;
    const isEnabledPayeverCalculator = isInstallmentEnabled && isFinancingEnabled && isCalculatorEnabled;
    const isAccessoryOrApparel = productType === ProductType.ACCESSORY || productType === ProductType.APPAREL;
    const isAccessory = productType === ProductType.ACCESSORY;
    const productCardLabels = useSelector(getProductCardLabels);
    const isNotifyMeEnabled = isUnit ? currentProduct?.enableNotifyMe ?? true : true;
    const { common_or_label: orLabel, common_lowStock_message: lowStockLabel } = productCardLabels;
    const lowStockAlert = useMemo(() => {
        if (!currentProduct || currentProduct.stock === 0) {
            return null;
        }
        const isPriceValid = Boolean(currentProduct?.prices && currentProduct?.prices?.amount > 0);
        const isAddToCardVisible = [
            currentProduct?.eCommerceEnabled,
            isPriceValid,
            ecommerceEnabled,
        ].every((item) => Boolean(item));
        if (!isAddToCardVisible) {
            return null;
        }
        const isLowStock = currentProduct.stock < LOW_STOCK_THRESHOLD;
        if (!isLowStock) {
            return null;
        }
        const msg = lowStockLabel.replace(COUNT_PLACEHOLDER, String(currentProduct.stock));
        return (React.createElement(Alert, { message: msg, type: AlertType.INFO, isFullWidth: true }));
    }, [currentProduct, ecommerceEnabled, lowStockLabel]);
    // Data from store (Filtered options)
    const { colorItemIndex, isVarianAvailable, sku } = useSelector(getProductFilterOptions);
    const holderRef = useRef(null);
    const cookieCompatibilityProduct = Cookies.get(COOKIE_HYPERDRIVE_SELECTED_UNIT);
    useEffect(() => {
        if (requiredProducts?.length && !addedRequiredProducts?.length && isAccessory) {
            setAccessoriesCartState(NEED_REQUIRED_PARTS);
            return;
        }
        if (checkCompatibilityProduct !== null &&
            !checkCompatibilityProduct?.isSuccessCheck &&
            isAccessory) {
            setAccessoriesCartState(INCOMPATIBLE);
            return;
        }
        if ((checkCompatibilityProduct === null || !cookieCompatibilityProduct) &&
            requiredProducts?.length &&
            isAccessory) {
            setAccessoriesCartState(NEED_CHECK);
            return;
        }
        setAccessoriesCartState('');
    }, [
        addedRequiredProducts?.length,
        checkCompatibilityProduct,
        cookieCompatibilityProduct,
        isAccessory,
        requiredProducts?.length,
    ]);
    const currProductArray = useMemo(() => (productVariantsByColor && productVariantsByColor[colorItemIndex]?.variantsOfProduct) || [], [colorItemIndex, productVariantsByColor]);
    useEffect(() => {
        const currProduct = currProductArray?.find((item) => item.sku === sku);
        if (currProduct) {
            setCurrentProduct(currProduct);
        }
    }, [colorItemIndex, sku, currProductArray]);
    if (!productVariantsByColor) {
        return null;
    }
    const { addToCartButtonLabel, outOfStockButtonLabel, requestTestRideButtonLabel } = getLabels(labels);
    const filterOptionByProductType = getFilterOptionByProductType({
        productVariantsByColor,
        colorItemIndex,
    });
    const blockWrapperWidth = holderRef?.current?.clientWidth;
    const masterProduct = getMasterProduct(productVariantsByColor);
    // Deps from master sku
    const eCommerceEnabledAttributeForMaster = masterProduct?.eCommerceEnabled;
    // Deps from current sku
    const priceAttribute = currentProduct?.prices;
    const stockAttribute = currentProduct?.stock;
    const eCommerceEnabledAttribute = currentProduct?.eCommerceEnabled;
    const productCampaignIDAttribute = currentProduct?.productCampaignID;
    const priceDiscountHistory = currentProduct.priceDiscountHistory;
    const sumOfAddedRequiredProductsPrices = addedRequiredProducts.reduce((sum, item) => sum + (item.price.discount?.discountedPrice ?? item.price.amount), 0);
    const currentProductPrice = priceAttribute?.discount?.discountedPrice ?? priceAttribute?.amount;
    const isDiscountExist = Boolean(priceAttribute?.discount);
    const totalItemPrice = currentProductPrice * productQuantity;
    const isDiscount = !!(currentProductPrice && priceAttribute.hasOwnProperty('discount'));
    const amountPrice = formatPrice({
        locale: rootLanguage,
        currencyName: priceAttribute?.currencyCode,
        price: productQuantity > 1 ? priceAttribute.amount * productQuantity : priceAttribute.amount,
    });
    const formattedPrice = getFormattedPrice({
        language,
        attr: {
            amount: sumOfAddedRequiredProductsPrices
                ? sumOfAddedRequiredProductsPrices + Number(totalItemPrice)
                : totalItemPrice,
            currencyCode: priceAttribute?.currencyCode,
            country: priceAttribute?.country,
            validFrom: priceAttribute?.validFrom,
            validUntil: priceAttribute?.validUntil,
            discount: priceAttribute?.discount,
        },
    });
    const hasValidTotalItemPrice = Number.isFinite(totalItemPrice) && totalItemPrice > 0;
    const isShownSantanderBanner = isInstallmentEnabled && isShowStub && isUnit && isFinancingEnabled && hasValidTotalItemPrice;
    const isAddedRequiredProductsExist = Boolean(addedRequiredProducts.length);
    const requiredProductResult = [
        {
            id: currentProduct?.sku,
            name: productName || '',
            price: priceAttribute,
        },
        ...addedRequiredProducts,
    ];
    const requiredProductSku = addedRequiredProducts.map((product) => product.sku); //here
    const preparedProductsUrls = prepareProductsUrls({
        currentSku: currentProduct.sku,
        currentUrl: pathname + hash,
        addedRequiredProducts,
    });
    let updatedPaymentOptions = [...paymentOptions];
    if (isEnabledPayeverCalculator || isShownSantanderBanner) {
        updatedPaymentOptions = [
            ...paymentOptions,
            {
                altText: SANTANDER,
                key: SANTANDER,
            },
        ];
    }
    const lastMonthDiscountObject = getLastMonthDiscountObject({
        locale: rootLanguage,
        priceAttribute,
        priceDiscountHistory,
        isOmnibusRegulationEnabled,
    });
    return (React.createElement(CardPanelWrapperStyled, { ref: holderRef },
        React.createElement(ProductFilter, { isAccessoryHideProductCompatibility: isAccessoryHideProductCompatibility, currentProduct: currentProduct, compatibilityAlert: compatibilityAlert, lowStockAlert: lowStockAlert, smartfitKey: smartfitKey, isRequiredProductExist: isRequiredProductExist, productType: productType, plpUrl: plpUrl, addRequiredProduct: addRequiredProduct, removeRequiredProduct: removeRequiredProduct, sizeGuideAssetPath: sizeGuideAssetPath, labels: labels, requiredProducts: requiredProducts, openSelectModelModal: openSelectModelModal, privacyConsentsModel: privacyConsentsModel, fasletModel: fasletModel, motorcyclesInfoLink: motorcyclesInfoLink, scooterPackagesInfoLink: scooterPackagesInfoLink }),
        React.createElement(NotifyMe, { sku: sku, stock: stockAttribute, labels: labels, isNotifyMeEnabled: isNotifyMeEnabled, productNotifyMeDisclaimer: productNotifyMeDisclaimer, privacyConsentsModel: privacyConsentsModel, eCommerceEnabledAttribute: eCommerceEnabledAttribute, eCommerceEnabledAttributeForMaster: eCommerceEnabledAttributeForMaster, productCampaignIDAttribute: productCampaignIDAttribute, isDiscountExist: isDiscountExist }),
        React.createElement(TestRideMessageForVariations, { productCampaignIDAttribute: productCampaignIDAttribute, eCommerceEnabledAttributeForMaster: eCommerceEnabledAttributeForMaster }),
        isAddedRequiredProductsExist && isAccessoryOrApparel && (React.createElement(AllProductUnitPrices, { rootLanguage: rootLanguage, products: requiredProductResult })),
        !isArchiveProduct && formattedPrice && priceAttribute?.amount > 0 ? (React.createElement(UnitPrice, { isMarginTop: !isAddedRequiredProductsExist, formattedPrice: formattedPrice, lastMonthDiscountObject: lastMonthDiscountObject, showDiscount: isDiscount && !(isAddedRequiredProductsExist && isAccessoryOrApparel), amountPrice: amountPrice, campaignDisclaimer: campaignDisclaimer })) : Boolean(campaignDisclaimer) ? (React.createElement(UnitPriceWrapperStyled, { isMarginTop: !isAddedRequiredProductsExist },
            React.createElement(PriceDisclaimerText, { campaignDisclaimer: campaignDisclaimer }))) : null,
        formattedPrice && priceAttribute?.amount > 0 && priceExtraInfo && (React.createElement(ProductYouFinanceInfo, { priceExtraInfo: priceExtraInfo, priceExtraInfoTooltip: priceExtraInfoTooltip })),
        React.createElement(ButtonsPanel
        // labels
        , { 
            // labels
            addToCartButtonLabel: addToCartButtonLabel, outOfStockButtonLabel: outOfStockButtonLabel, requestTestRideButtonLabel: requestTestRideButtonLabel, 
            // extra props
            hyperdriveEndpoints: hyperdriveEndpoints, filterOptionByProductType: filterOptionByProductType, variantSku: currentProduct.sku, requiredProductSku: requiredProductSku, selectModelHandler: openSelectModelModal, isAccessoryOrApparel: isAccessoryOrApparel, accessoriesCartState: accessoriesCartState, preparedProductsUrls: preparedProductsUrls, 
            // visible 'add to cart'
            isVarianAvailable: isVarianAvailable, isMaxStockValue: isMaxStockValue, productStock: stockAttribute, priceAttribute: priceAttribute, eCommerceEnabledVariant: eCommerceEnabledAttribute, productCampaignIDAttribute: productCampaignIDAttribute, blockWrapperWidth: blockWrapperWidth }),
        isEnabledPayeverCalculator && (React.createElement(PayeverCalculator, { payeverCalculatorConfig: payeverCalculatorConfig, isAvailableWidget: hasValidTotalItemPrice, amount: totalItemPrice, icon: React.createElement(SVGIcon, { pathFill: colorCoreBlack900Base, type: InfoOutline, size: ICON_SIZE.xs }), textPrompt: labels.productCard_santanderAvailability_prompt })),
        isShownSantanderBanner && (React.createElement(ProductBanner, { headerTextNode: React.createElement(RegularTextLevel3, { color: colorCoreBlack300, weight: 400 }, payeverCalculator_stubHeader_label), separatorText: payeverCalculator_stubHeader_label, description: payeverCalculator_stubDescription_label, icon: React.createElement(SVGIcon, { rectFill: "none", size: ICON_SIZE.m, type: santander }) })),
        isShownLeasingPayments && (React.createElement(LeasingPayments, { isAuthorized: isAuthorized, sku: sku, separatorText: orLabel })),
        isShownCycleScheme && (React.createElement(CycleSchemeBanner, { bannerIcon: CycleScheme, icon: React.createElement(SVGIcon, { pathFill: colorCoreBlack900Base, type: InfoOutline, size: ICON_SIZE.xs }) })),
        ecommerceEnabled && eCommerceEnabledAttribute && (React.createElement(PaymentOptionsStyled, { label: commonLabels.common_paymentOptions_label, paymentOptions: updatedPaymentOptions })),
        warrantyInfo && React.createElement(WarrantyInformation, { items: warrantyInfo }),
        Boolean(downloadSectionItems.length) && (React.createElement(FlexWrapper, { isFullWidth: true, mt: "32px", mtDesktop: "48px" },
            React.createElement(DownloadSectionComponent, { files: downloadSectionItems, title: t('downloadSection_header_label'), isRightToLeft: isRightToLeft, isTargetBlank: true })))));
}
CardPanel.displayName = 'CardPanel_Component';
